import { Button, Grid, Icon, Spinner, Typography } from 'gu-ui-kit'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Referral/ReferralLinks/filters'
import FilterPanel from 'components/Filters/FilterPanel'
import { useReferralLinks } from 'modules/Referral/ReferralLinks/hooks/useReferralLinks'
import GridSpinner from 'components/UI/GridSpinner'
import { ROUTES } from 'constants/routes'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { LINKS_SET_FILTERS } from 'modules/Referral/ReferralLinks/types'
import { Table } from 'modules/Referral/ReferralLinks/subcomponents'
import isEmpty from 'lodash/isEmpty'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as EmptyResultsSVG } from 'images/illustration/no-referral-links.svg'
import PageHeader from 'components/PageHeader'

const ReferralLinks = () => {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const [firstLoad, setFirstLoad] = useState(true)
  const [linksCreated, setLinksCreated] = useState(false)

  const { fetchLinks, updateLink, resetLinks } = useReferralLinks()

  const breakpoints = useBreakpoint()

  const pathGen = useGeneratePathI18n()

  const {
    filters,
    data: links,
    pagination,
  } = useSelector((state) => ({
    ...state['referral/links'],
  }))

  const filterManager = useFiltersManager({
    filters,
    config: config(),
    setAction: LINKS_SET_FILTERS,
    localStorageKey: 'referral-links',
  })

  useEffect(() => {
    setPage(1)
  }, [filterManager.params])

  useEffect(() => {
    if (!filterManager.params) {
      return
    }

    fetchLinks({ page })
  }, [filterManager.params, page])

  const handleChangeName = useCallback(
    (id, name) => updateLink({ id, data: { name } }),
    [],
  )

  // reset data after exit links page
  useEffect(() => resetLinks, [])

  useEffect(
    () =>
      firstLoad &&
      setLinksCreated(
        (isEmpty(filterManager.params) &&
          fetchLinks.finished &&
          pagination?.total > 0) ||
          !isEmpty(filterManager.params),
      ),
    [filterManager.params, fetchLinks, pagination],
  )

  useEffect(
    () => fetchLinks.finished && setFirstLoad(false),
    [fetchLinks.finished, pagination],
  )

  return (
    <Grid direction={'column'}>
      <PageHeader
        color={'Label-Primary'}
        className={breakpoints.fbs ? 'margin-bottom-8' : 'margin-bottom-16'}
      >
        {t('referral::referral_links.heading')}
      </PageHeader>
      <Typography
        variant={'Body-20'}
        color={'Label-Secondary'}
        component={'p'}
        className={'margin-bottom-24'}
        noMargin
      >
        {t('referral::referral_links.summary')}
      </Typography>

      {firstLoad && fetchLinks.progress && <GridSpinner width={32} />}

      {!linksCreated && !firstLoad && (
        <EmptyResults
          header={t('referral::referral_links.empty_list.heading')}
          text={
            <Grid alignItems={'center'} direction={'column'} gap={32}>
              <Typography variant={'Body-25'} color={'Label-Fifth'}>
                {t('referral::referral_links.empty_list.summary')}
              </Typography>
              <Button
                fullWidth={breakpoints.fbs}
                component={Link}
                to={pathGen(ROUTES.REFERRAL_LINKS_CREATE)}
                variant={'primary'}
                size="l"
                startIcon={<Icon name={'plus'} />}
              >
                {t('referral::referral_links.create_link')}
              </Button>
            </Grid>
          }
          image={<EmptyResultsSVG />}
        />
      )}

      {linksCreated && !firstLoad && (
        <>
          <div className={'margin-bottom-40'}>
            <Button
              fullWidth={breakpoints.fbs}
              component={Link}
              to={pathGen(ROUTES.REFERRAL_LINKS_CREATE)}
              variant={'primary'}
              size="l"
              startIcon={<Icon name={'plus'} />}
            >
              {t('referral::referral_links.create_link')}
            </Button>
          </div>
          <div className={'margin-bottom-32'}>
            {filterManager.filters && (
              <FilterPanel filterManager={filterManager} />
            )}
          </div>
          {fetchLinks.progress && (
            <GridSpinner width={32} style={{ height: '100%' }} />
          )}
          {links && ((!fetchLinks.progress && page === 1) || page > 1) && (
            <Table data={links} handleChangeName={handleChangeName} />
          )}
          {links?.length > 0 && pagination?.total !== links.length && (
            <Grid
              className={'margin-top-40 margin-bottom-20'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              {pagination.total !== links.length && (
                <Button
                  onClick={() => setPage(page + 1)}
                  variant={'link'}
                  startIcon={
                    fetchLinks.progress ? (
                      <Spinner width={16} color={'inherit'} />
                    ) : null
                  }
                  endIcon={
                    <Icon name={'arrow1-down'} color={'inherit'} size={'s'} />
                  }
                >
                  {t('common::pagination.show_more')}
                </Button>
              )}
            </Grid>
          )}
        </>
      )}
    </Grid>
  )
}

export default ReferralLinks
